import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { cartCheckoutSubtotal, cartMerchandiseSubtotal, cartOutOfTownSubtotal, normalizeOutOfTownDays } from '../utils/outOfTownSurcharge';

const SS_MODE = 'icct_public_donate_mode';
const SS_TOKEN = 'icct_public_donate_link_token';
const SS_EMAIL = 'icct_public_donate_email';
export const PUBLIC_DONATE_CART_STORAGE_KEY = 'icct_public_donate_cart_v1';

const PublicDonateContext = createContext(null);

function readSs(key) {
  try {
    return sessionStorage.getItem(key) || '';
  } catch {
    return '';
  }
}

function readCartFromSession() {
  try {
    const raw = sessionStorage.getItem(PUBLIC_DONATE_CART_STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter(Boolean) : [];
  } catch {
    return [];
  }
}

export function PublicDonateProvider({ children }) {
  const [cart, setCart] = useState(() => readCartFromSession());
  const [donateMode, setDonateModeState] = useState(() => readSs(SS_MODE) || null);
  const [linkToken, setLinkTokenState] = useState(() => readSs(SS_TOKEN));
  const [verifiedEmail, setVerifiedEmailState] = useState(() => readSs(SS_EMAIL));

  const setGuestMode = useCallback(() => {
    try {
      sessionStorage.setItem(SS_MODE, 'guest');
      sessionStorage.removeItem(SS_TOKEN);
      sessionStorage.removeItem(SS_EMAIL);
    } catch {}
    setDonateModeState('guest');
    setLinkTokenState('');
    setVerifiedEmailState('');
  }, []);

  const setLinkedMode = useCallback((tok, email) => {
    const e = String(email || '').trim().toLowerCase();
    try {
      sessionStorage.setItem(SS_MODE, 'linked');
      sessionStorage.setItem(SS_TOKEN, String(tok || '').trim());
      sessionStorage.setItem(SS_EMAIL, e);
    } catch {}
    setCart([]);
    setDonateModeState('linked');
    setLinkTokenState(String(tok || '').trim());
    setVerifiedEmailState(e);
  }, []);

  const clearDonateSession = useCallback(() => {
    try {
      sessionStorage.removeItem(SS_MODE);
      sessionStorage.removeItem(SS_TOKEN);
      sessionStorage.removeItem(SS_EMAIL);
      sessionStorage.removeItem(PUBLIC_DONATE_CART_STORAGE_KEY);
    } catch {}
    setDonateModeState(null);
    setLinkTokenState('');
    setVerifiedEmailState('');
    setCart([]);
  }, []);

  useEffect(() => {
    try {
      if (!cart.length) sessionStorage.removeItem(PUBLIC_DONATE_CART_STORAGE_KEY);
      else sessionStorage.setItem(PUBLIC_DONATE_CART_STORAGE_KEY, JSON.stringify(cart));
    } catch {
      /* ignore */
    }
  }, [cart]);

  const addItem = (item) => {
    const productId = Number(item?.product_id || 0);
    const variationId = Number(item?.variation_id || 0);
    const quantity = Math.max(1, parseInt(item?.quantity, 10) || 1);
    if (!productId) return;
    setCart((prev) => {
      const idx = prev.findIndex(
        (x) => Number(x.product_id) === productId && Number(x.variation_id || 0) === variationId
      );
      if (idx >= 0) {
        const next = [...prev];
        const merged = {
          ...next[idx],
          quantity: Math.max(1, Number(next[idx].quantity || 1) + quantity),
        };
        if (item && Object.prototype.hasOwnProperty.call(item, 'out_of_town_days')) {
          const days = normalizeOutOfTownDays(item.out_of_town_days);
          if (days > 0) merged.out_of_town_days = days;
          else delete merged.out_of_town_days;
        }
        next[idx] = merged;
        return next;
      }
      const row = {
        product_id: productId,
        variation_id: variationId,
        quantity,
        product_name: String(item?.product_name || '').trim(),
        variation_name: String(item?.variation_name || '').trim(),
        unit_price: Number(item?.unit_price || 0),
      };
      const days = normalizeOutOfTownDays(item?.out_of_town_days);
      if (days > 0) row.out_of_town_days = days;
      return [...prev, row];
    });
  };

  const updateQuantity = (productId, variationId, quantity) => {
    const qty = Math.max(1, parseInt(quantity, 10) || 1);
    setCart((prev) =>
      prev.map((x) =>
        Number(x.product_id) === Number(productId) && Number(x.variation_id || 0) === Number(variationId)
          ? { ...x, quantity: qty }
          : x
      )
    );
  };

  const updateOutOfTownDays = (productId, variationId, days) => {
    const d = normalizeOutOfTownDays(days);
    setCart((prev) =>
      prev.map((x) => {
        if (Number(x.product_id) !== Number(productId) || Number(x.variation_id || 0) !== Number(variationId)) {
          return x;
        }
        const next = { ...x };
        if (d > 0) next.out_of_town_days = d;
        else delete next.out_of_town_days;
        return next;
      })
    );
  };

  const removeItem = (productId, variationId) => {
    setCart((prev) =>
      prev.filter(
        (x) =>
          !(Number(x.product_id) === Number(productId) && Number(x.variation_id || 0) === Number(variationId))
      )
    );
  };

  const clearCart = () => {
    try {
      sessionStorage.removeItem(PUBLIC_DONATE_CART_STORAGE_KEY);
    } catch {
      /* ignore */
    }
    setCart([]);
  };

  const merchandiseSubtotal = useMemo(() => cartMerchandiseSubtotal(cart), [cart]);
  const outOfTownSubtotal = useMemo(() => cartOutOfTownSubtotal(cart), [cart]);
  const totalAmount = useMemo(() => cartCheckoutSubtotal(cart), [cart]);
  const itemCount = useMemo(
    () => cart.reduce((sum, i) => sum + Math.max(1, parseInt(i.quantity, 10) || 1), 0),
    [cart]
  );

  const value = {
    cart,
    addItem,
    updateQuantity,
    removeItem,
    clearCart,
    totalAmount,
    merchandiseSubtotal,
    outOfTownSubtotal,
    updateOutOfTownDays,
    itemCount,
    donateMode,
    linkToken,
    verifiedEmail,
    setGuestMode,
    setLinkedMode,
    clearDonateSession,
  };

  return <PublicDonateContext.Provider value={value}>{children}</PublicDonateContext.Provider>;
}

export function usePublicDonate() {
  const ctx = useContext(PublicDonateContext);
  if (!ctx) throw new Error('usePublicDonate must be used within PublicDonateProvider');
  return ctx;
}
